import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FingoDataGrid } from '../../../../components/dataGrids';
import useIsMobile from '../../../../hooks/useIsMobile';
import { useFilteredQuery } from '../../../../hooks/useFilteredQuery';
import useSelectedCompany from '../../../../hooks/useSelectedCompany';
import { useAdditionalQueries } from '../../../../hooks/useAdditionalQueries';
import { DETAILED_DATA_GRID_CLASS_NAME } from '../../../../theme/constants/data-grid';

const FactoringSummary = ({
  selectedDocuments,
  dataGridClassName,
  queryDocument,
  defaultOrderBy,
  summaryHeaders,
  mobileHeaders,
  onSelectionModelChange,
  initialPageSize,
  mobileDensity,
  additionalQueries,
  getColumns,
}) => {
  const isMobile = useIsMobile('tablet');
  const handleDelete = (invoiceId) => {
    const newSelectedIds = selectedDocuments.filter((id) => id !== invoiceId);
    onSelectionModelChange(newSelectedIds);
  };
  const columns = getColumns(
    isMobile,
    handleDelete,
  );
  const onCompletedSetRows = (data) => data.invoices.edges.map((edge) => (edge.node));
  const onCompletedSetLength = (data) => data.invoices.totalCount;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const selectedCompany = useSelectedCompany();

  const {
    data: rawData,
    loading,
  } = useFilteredQuery(
    queryDocument,
    {
      variables: {
        id_In: selectedDocuments,
        first: pageSize,
        offset: page * pageSize,
        orderBy,
        companyId: selectedCompany.id,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    },
  );

  const data = rawData ? onCompletedSetRows(rawData) : [];
  const rowCount = rawData ? onCompletedSetLength(rawData) : 0;

  const commonVars = useMemo(() => ({
    id_In: selectedDocuments,
    first: pageSize,
    offset: page * pageSize,
    orderBy,
    companyId: selectedCompany?.id,
  }), [selectedDocuments, pageSize, page, orderBy, selectedCompany?.id]);

  const additionalQueriesResults = useAdditionalQueries({
    additionalQueries,
    commonVars,
    data,
  });

  return (
    <FingoDataGrid
      dataGridClassName={dataGridClassName}
      loadingWithSkeleton={loading}
      rows={data}
      columns={columns}
      includeHeaders={summaryHeaders}
      mobileHeaders={mobileHeaders}
      rowsPerPageOptions={[initialPageSize]}
      rowCount={rowCount}
      pageSize={pageSize}
      disableSelectionOnClick
      setOrderBy={setOrderBy}
      orderBy={orderBy}
      page={page}
      onPageChange={setPage}
      onPageSizeChange={setPageSize}
      checkboxSelection={false}
      loading={loading}
      mobileDensity={mobileDensity}
      rowHeight={65}
      additionalQueriesResults={additionalQueriesResults}
      outsideBoxProps={{
        sx: {
          height: loading ? '100%' : 'auto',
        },
      }}
    />
  );
};

FactoringSummary.propTypes = {
  selectedDocuments: PropTypes.arrayOf(PropTypes.string).isRequired,
  dataGridClassName: PropTypes.string,
  queryDocument: PropTypes.shape().isRequired,
  defaultOrderBy: PropTypes.string,
  summaryHeaders: PropTypes.arrayOf(PropTypes.string).isRequired,
  mobileHeaders: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectionModelChange: PropTypes.func.isRequired,
  initialPageSize: PropTypes.number,
  mobileDensity: PropTypes.string,
  additionalQueries: PropTypes.objectOf(PropTypes.shape({})),
  getColumns: PropTypes.func.isRequired,
};

FactoringSummary.defaultProps = {
  dataGridClassName: DETAILED_DATA_GRID_CLASS_NAME,
  defaultOrderBy: '',
  initialPageSize: 10,
  mobileDensity: 'standard',
  additionalQueries: {},
};

export default FactoringSummary;
